/* eslint-disable no-unused-expressions */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  CheckCircleFilled,
  DeleteOutlined,
  FolderOutlined,
  FullscreenExitOutlined,
  LoadingOutlined,
  SaveOutlined,
  UploadOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { Button, Form, Modal, Progress, Spin, TimePicker } from 'antd';
import Text from 'antd/lib/typography/Text';
import moment from 'moment';
import React, {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import InputTag from '../../components/InputTag';
import * as SERVICE from '../../constants/service.types';
import { DECODED_ACCESS_TOKEN, NOTIFIES } from '../../constants/system.keys';
import {
  getResetUploadFileReturn,
  openMessage,
  setOpenUploadFilesModal,
  setUploadFilesCamera,
  setUploadFilesDate,
  setUploadFilesOldVideos,
  socketEmitEvent,
} from '../../redux/actions';
import './index.scss';
import { beeXLogger } from '../../helpers/logger';
import { sliceFileToMultiParts } from './utils';
import {
  getUploadMultiIdApi,
  getUploadPreSignPartUrlApi,
  completeUploadMultipartApi,
  checkCompleteUploadVideoApi,
  getUploadMultipartThumbnailApi,
  reportUploadMultipartApi,
} from '../../api/uploadApi';
import storageService from '../../services/storage.service';
import { nanoid } from 'nanoid';
import { getDeviceToken } from '../../hooks/socket/cameras';

const layout = {
  labelCol: { span: 7 },
  wrapperCol: { span: 17 },
};

const MAX_RETRIES = 3;
const UPLOAD_TIMEOUT = 10000;
const MAX_CONCURRENT_PARTS = 4;
const RETRY_BASE_DELAY = 1000;
const RETRY_MAX_DELAY = 10000;
const PRESIGNED_URL_EXPIRE_SAFETY_MS = 60000;

const UploadVideoCameraV2 = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    uploadFilesCamera,
    uploadFilesDate,
    uploadFilesOldVideos,
    namespace,
    requestUploadFile,
    uploadStorageFile,
    socketState,
  } = useSelector((state) => state);
  const [isCollapse, setIsCollapse] = useState(false);
  const [isLoad, setIsLoad] = useState(false);
  const [isUploadFilesSuccess, setIsUploadFilesSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingReadFiles, setIsLoadingReadFiles] = useState(false);

  const [form] = Form.useForm();

  const currentFile = useRef(null);
  const [isSubmittedForm, setIsSubmittedForm] = useState(false);

  const inputUploadFileRef = useRef();
  const inputUploadFolderRef = useRef();
  const [filePreSignUrl, setFilePreSignUrl] = useState([]);
  const uploadInProgressRef = useRef(false);
  const fileAbortControllersRef = useRef({});

  const [cameraToken, setCameraToken] = useState('');

  const createAbortError = useCallback((message) => {
    const abortError = new Error(message || t('Upload cancelled by user'));
    abortError.code = 'ABORTED';
    return abortError;
  }, []);

  const handleFileAbortController = useCallback((fileId) => {
    if (!fileId) return null;

    if (!fileAbortControllersRef.current[fileId]) {
      fileAbortControllersRef.current[fileId] = {
        isCancelled: false,
        reason: null,
        activeXhrs: new Set(),
      };
    }

    return fileAbortControllersRef.current[fileId];
  }, []);

  const clearFileAbortController = useCallback((fileId) => {
    if (!fileId) return;
    delete fileAbortControllersRef.current[fileId];
  }, []);

  const abortFileUploadById = useCallback(
    (fileId, reason = t('Upload cancelled by user')) => {
      const fileAbortController = handleFileAbortController(fileId);
      if (!fileAbortController) return;

      fileAbortController.isCancelled = true;
      fileAbortController.reason = reason;

      Array.from(fileAbortController.activeXhrs).forEach((xhr) => {
        try {
          xhr.abort();
        } catch (abortErr) {
          beeXLogger.warn(
            `UploadVideoCameraV2 - Failed aborting xhr for file ${fileId}`,
            abortErr,
          );
        }
      });
    },
    [handleFileAbortController, t],
  );

  const handleCancelSingleFileUpload = useCallback(
    (fileId) => {
      if (!fileId) return;

      abortFileUploadById(fileId);

      const currentFileIndex = form
        .getFieldValue('listFileVideo')
        ?.findIndex((item) => item.id === fileId || item.fileId === fileId);

      if (currentFileIndex !== undefined && currentFileIndex !== -1) {
        form.setFields([
          {
            name: ['listFileVideo', currentFileIndex, 'status'],
            value: {
              loadingPercent: null,
              isUploadSuccess: false,
              error: t('Upload cancelled by user'),
            },
          },
        ]);
      }
    },
    [abortFileUploadById, form, t],
  );

  useEffect(() => {
    if (uploadFilesCamera) {
      getDeviceToken({
        serial: uploadFilesCamera.serial,
        namespace,
      })
        .then((res) => {
          beeXLogger.info('🚀 ~ getDeviceToken ~ res:', res);
          if (!res || res?.serial !== uploadFilesCamera.serial)
            setCameraToken('');
          else setCameraToken(res?.deviceToken);
        })
        .catch((err) => {
          beeXLogger.error('🚀 ~ getDeviceToken ~ err:', err);
        });
    }
  }, []);

  const dataVideoTimes = useMemo(
    () =>
      uploadFilesOldVideos?.map((item) => ({
        from: moment(
          `${moment(uploadFilesDate).format('DD-MM-YYYY')} ${item.from}`,
          'DD-MM-YYYY HH:mm:ss',
        ).valueOf(),
        to: moment(
          `${moment(uploadFilesDate).format('DD-MM-YYYY')} ${item.to}`,
          'DD-MM-YYYY HH:mm:ss',
        ).valueOf(),
      })) ?? [],
    [uploadFilesDate, uploadFilesOldVideos],
  );

  const handleErrorWhileUploading = (errMessage) => {
    const currentFileIndex = form
      .getFieldValue('listFileVideo')
      ?.findIndex((item) => item.id === currentFile.current?.id);
    if (currentFileIndex !== undefined) {
      form.setFields([
        {
          name: ['listFileVideo', currentFileIndex, 'status'],
          value: {
            loadingPercent: null,
            isUploadSuccess: false,
            error: errMessage,
          },
        },
      ]);
    }
    setError(errMessage);
    setIsLoad(false);
  };

  useEffect(() => {
    if (socketState === -1) {
      handleErrorWhileUploading(t('Lost connection to server'));
    }
  }, [socketState]);

  const findLastStartTime = () => {
    return dataVideoTimes?.length
      ? moment.max(
          uploadFilesOldVideos.map((item) =>
            moment(
              `${moment(uploadFilesDate).format('DD-MM-YYYY')} ${item.to}`,
              'DD-MM-YYYY HH:mm:ss',
            ),
          ),
        )
      : moment(uploadFilesDate).startOf('day');
  };

  const getUploadTimestampFromFileTime = useCallback(
    (fileTime) => {
      const selectedTime = moment(fileTime);
      const baseDate = moment(uploadFilesDate);

      if (!baseDate.isValid()) {
        return selectedTime.isValid() ? selectedTime.valueOf() : Date.now();
      }

      if (!selectedTime.isValid()) {
        return baseDate.startOf('day').valueOf();
      }

      return baseDate
        .set({
          hour: selectedTime.hour(),
          minute: selectedTime.minute(),
          second: 0,
          millisecond: 0,
        })
        .valueOf();
    },
    [uploadFilesDate],
  );

  const createThumbnailFromVideo = (videoFile) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      video.preload = 'metadata';
      video.muted = true;

      video.onloadedmetadata = () => {
        video.currentTime = Math.min(1, video.duration * 0.1);
      };

      video.onseeked = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create thumbnail blob'));
            }
          },
          'image/jpeg',
          0.8,
        );
      };

      video.onerror = () => {
        reject(new Error('Failed to load video for thumbnail'));
      };

      video.src = URL.createObjectURL(videoFile);
    });
  };

  const handleUploadThumbnailToS3 = async (thumbnailBlob, uploadUrl) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Type', 'image/jpeg');

      xhr.onload = () => {
        if (xhr.status === 200) {
          const etag =
            xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag');

          if (!etag) {
            beeXLogger.warn(
              'UploadVideoCameraV2 - Cannot read ETag header - check S3 CORS ExposeHeaders config',
            );
          }
          resolve(etag?.replace(/"/g, '') || null);
        } else {
          reject(new Error('Upload thumbnail failed'));
        }
      };

      xhr.onerror = () => reject(new Error('Upload thumbnail failed'));

      xhr.send(thumbnailBlob);
    });
  };

  const handleGetThumbnailAndUpload = async (fileData, timestamp) => {
    try {
      const thumbnailObjectName = fileData.objectName.replace('.mp4', '.jpg');

      beeXLogger.debug('UploadVideoCameraV2 - Getting thumbnail upload URL...');

      const thumbnailResponse = await getUploadMultipartThumbnailApi(
        cameraToken,
        {},
        {
          deviceSerial: uploadFilesCamera?.serial,
          namespace,
          objectName: thumbnailObjectName,
          contentType: 'image',
          timestamp: timestamp,
        },
      );

      if (
        thumbnailResponse?.status === 200 &&
        thumbnailResponse?.data?.uploadUrl
      ) {
        beeXLogger.debug(
          'UploadVideoCameraV2 - Creating thumbnail from video...',
        );

        const thumbnailBlob = await createThumbnailFromVideo(
          fileData.originFile,
        );

        beeXLogger.debug('UploadVideoCameraV2 - Uploading thumbnail to S3...');

        await handleUploadThumbnailToS3(
          thumbnailBlob,
          thumbnailResponse.data.uploadUrl,
        );

        beeXLogger.debug(
          'UploadVideoCameraV2 - Thumbnail uploaded successfully',
        );

        return thumbnailResponse.data.uploadUrl;
      } else {
        throw new Error('Failed to get thumbnail upload URL');
      }
    } catch (error) {
      beeXLogger.error(
        'UploadVideoCameraV2 - Error handling thumbnail:',
        error,
      );
      // throw error;
    }
  };

  const handleReportUpload = async (
    fileData,
    thumbnailUrl,
    videoDuration,
    timestamp,
  ) => {
    try {
      beeXLogger.debug('UploadVideoCameraV2 - Reporting upload...');

      const storageConfig = {
        accessKey: '',
        bucketName: fileData.bucket,
        credentialId: fileData.credentialId,
        encryption: '',
        endpoint: '',
        objectName: fileData.objectName,
        region: 'us-east-1',
        secretKey: '',
      };

      const reportData = {
        action: 'record-camera',
        bandwidth: Math.round((fileData.originFile.size * 8) / videoDuration),
        codec: 'H264',
        deviceSerial: uploadFilesCamera?.serial,
        duration: Math.round(videoDuration),
        pathFile: fileData.objectName,
        resolution: '448',
        size: fileData.originFile.size,
        storageConfig: JSON.stringify(storageConfig),
        storageType: 's3',
        thumbnail: thumbnailUrl || 'https://temp',
        namespace,
        timestamp,
      };
      beeXLogger.debug('🚀 ~ handleReportUpload ~ reportData:', reportData);

      const reportResponse = await reportUploadMultipartApi(
        cameraToken,
        {},
        reportData,
      );

      if (reportResponse?.status === 200) {
        beeXLogger.debug('UploadVideoCameraV2 - Upload reported successfully');
        return true;
      } else {
        throw new Error('Failed to report upload');
      }
    } catch (error) {
      beeXLogger.error('UploadVideoCameraV2 - Error reporting upload:', error);
      // throw error;
    }
  };

  const handleSubmitUploadVideos = async (formValues) => {
    beeXLogger.debug(
      'UploadVideoCameraV2 - handleSubmitUploadVideos - formValues: ',
      formValues,
    );

    if (formValues.listFileVideo.length > 0 && !uploadInProgressRef.current) {
      uploadInProgressRef.current = true;
      setIsUploadFilesSuccess(false);
      setError(null);
      setIsLoad(true);
      setIsSubmittedForm(true);

      const filesToUpload = formValues.listFileVideo.filter(
        (item) => !item.status.isUploadSuccess,
      );

      beeXLogger.debug(
        'UploadVideoCameraV2 - Files to upload:',
        filesToUpload.length,
      );

      const filesWithPreSignUrls = [];
      for (const file of filesToUpload) {
        try {
          beeXLogger.debug(
            'UploadVideoCameraV2 - Getting presign URLs for file:',
            file.id,
          );

          const newParts = await Promise.all(
            file?.parts?.map(async (item) => {
              const preSignUrlRes = await getUploadPreSignPartUrlApi(
                cameraToken,
                {},
                {
                  objectName: file?.objectName,
                  uploadId: item?.uploadId,
                  partNumber: item?.partNumber,
                  contentType: 'video/mp4',
                  namespace,
                  credentialId: file?.credentialId,
                },
              );
              return {
                ...item,
                ...preSignUrlRes?.data,
              };
            }) || [],
          );

          filesWithPreSignUrls.push({
            ...file,
            parts: newParts,
          });
        } catch (error) {
          beeXLogger.error('Error getting presign URL for file:', error);
          const currentFileIndex = formValues.listFileVideo.findIndex(
            (item) => item.id === file.id,
          );
          if (currentFileIndex !== -1) {
            form.setFields([
              {
                name: ['listFileVideo', currentFileIndex, 'status'],
                value: {
                  loadingPercent: null,
                  isUploadSuccess: false,
                  error:
                    error.message ||
                    t('There was an error while uploading the file'),
                },
              },
            ]);
          }
        }
      }

      if (filesWithPreSignUrls.length > 0) {
        beeXLogger.debug(
          'UploadVideoCameraV2 - Starting upload for files:',
          filesWithPreSignUrls.length,
        );
        setFilePreSignUrl(filesWithPreSignUrls);
      } else {
        uploadInProgressRef.current = false;
        setIsLoad(false);
      }
    }
  };

  const getVideoDuration = (videoFile) => {
    return new Promise((resolve) => {
      let video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = function () {
        resolve(video.duration);
      };
      video.onerror = function () {
        resolve(null);
      };
      video.src = window.URL.createObjectURL(videoFile);
    });
  };

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const getRetryDelay = (retryCount) =>
    Math.min(RETRY_BASE_DELAY * 2 ** retryCount, RETRY_MAX_DELAY);

  const isPresignedUrlExpired = (uploadUrl) => {
    try {
      if (!uploadUrl) return true;

      const parsedUrl = new URL(uploadUrl);
      const amzDate = parsedUrl.searchParams.get('X-Amz-Date');
      const amzExpires = Number(parsedUrl.searchParams.get('X-Amz-Expires'));

      if (!amzDate || Number.isNaN(amzExpires)) return false;

      const signedAt = moment.utc(amzDate, 'YYYYMMDDTHHmmss[Z]', true);
      if (!signedAt.isValid()) return false;

      const expiresAt = signedAt.clone().add(amzExpires, 'seconds').valueOf();
      return Date.now() + PRESIGNED_URL_EXPIRE_SAFETY_MS >= expiresAt;
    } catch (error) {
      beeXLogger.warn(
        'UploadVideoCameraV2 - Failed to parse presigned URL expiry',
        error,
      );
      return false;
    }
  };

  const shouldRefreshPresignedUrl = (uploadUrl, error) => {
    if (!uploadUrl || isPresignedUrlExpired(uploadUrl)) {
      return true;
    }

    if (error?.status === 403) {
      return true;
    }

    const errorPayload = `${error?.message || ''} ${error?.responseText || ''}`
      .toLowerCase()
      .trim();

    return (
      errorPayload.includes('expired') ||
      errorPayload.includes('request has expired') ||
      errorPayload.includes('signaturedoesnotmatch') ||
      errorPayload.includes('invalid signature') ||
      errorPayload.includes('token')
    );
  };

  const getFreshPartUploadUrl = async (fileItem, part) => {
    const preSignUrlRes = await getUploadPreSignPartUrlApi(
      cameraToken,
      {},
      {
        objectName: fileItem?.objectName,
        uploadId: part?.uploadId,
        partNumber: part?.partNumber,
        contentType: 'video/mp4',
        namespace,
        credentialId: fileItem?.credentialId,
      },
    );

    const uploadUrl = preSignUrlRes?.data?.uploadUrl;
    if (!uploadUrl) {
      throw new Error(
        `Failed to get presigned URL for part ${part?.partNumber || 'unknown'}`,
      );
    }

    return uploadUrl;
  };

  const uploadPartToS3 = (
    partBlob,
    onProgress,
    uploadUrl,
    fileAbortController,
  ) => {
    if (!uploadUrl) {
      const missingUrlError = new Error('Missing uploadUrl for part');
      missingUrlError.code = 'MISSING_UPLOAD_URL';
      return Promise.reject(missingUrlError);
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let lastProgressTime = Date.now();
      let lastLoadedBytes = 0;
      let lastUpdateTime = 0;
      let timeoutMonitorId = null;
      let isTimeoutAbort = false;

      const clearTimeoutMonitor = () => {
        if (timeoutMonitorId !== null) {
          clearInterval(timeoutMonitorId);
          timeoutMonitorId = null;
        }
      };

      const removeCurrentXhrFromTracker = () => {
        fileAbortController?.activeXhrs?.delete(xhr);
      };

      const cleanup = () => {
        clearTimeoutMonitor();
        removeCurrentXhrFromTracker();
      };

      const rejectWithError = (error) => {
        cleanup();
        reject(error);
      };

      if (fileAbortController?.isCancelled) {
        rejectWithError(
          createAbortError(fileAbortController.reason || 'Upload cancelled'),
        );
        return;
      }

      const checkTimeout = () => {
        const timeSinceLastProgress = Date.now() - lastProgressTime;
        if (timeSinceLastProgress > UPLOAD_TIMEOUT) {
          isTimeoutAbort = true;
          xhr.abort();
        }
      };

      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const currentTime = Date.now();

          if (event.loaded > lastLoadedBytes) {
            lastProgressTime = currentTime;
            lastLoadedBytes = event.loaded;
          }

          if (currentTime - lastUpdateTime > 300) {
            lastUpdateTime = currentTime;
            const percentComplete =
              event.total > 0
                ? Math.round((event.loaded / event.total) * 100)
                : 0;
            onProgress(percentComplete);
          }
        }
      };

      xhr.onload = () => {
        cleanup();
        if (xhr.status === 200) {
          onProgress(100);
          const etag =
            xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag');
          resolve(etag?.replace(/"/g, ''));
        } else {
          const httpError = new Error(
            `Upload part failed with status ${xhr.status}`,
          );
          httpError.code = 'HTTP_ERROR';
          httpError.status = xhr.status;
          httpError.responseText = xhr.responseText;
          reject(httpError);
        }
      };

      xhr.onerror = () => {
        const networkError = new Error('Upload part network error');
        networkError.code = 'NETWORK';
        rejectWithError(networkError);
      };

      xhr.onabort = () => {
        if (isTimeoutAbort) {
          const timeoutError = new Error('Upload part timed out');
          timeoutError.code = 'TIMEOUT';
          rejectWithError(timeoutError);
        } else {
          rejectWithError(
            createAbortError(
              fileAbortController?.reason || 'Upload part aborted by user',
            ),
          );
        }
      };

      fileAbortController?.activeXhrs?.add(xhr);
      timeoutMonitorId = setInterval(checkTimeout, 1000);

      if (fileAbortController?.isCancelled) {
        xhr.abort();
        return;
      }

      xhr.send(partBlob);
    });
  };

  const handleUploadPartToS3WithRetry = async (
    fileItem,
    part,
    onProgress,
    fileAbortController,
    retryCount = 0,
    currentUploadUrl = part?.uploadUrl,
  ) => {
    let uploadUrl = currentUploadUrl;

    try {
      if (fileAbortController?.isCancelled) {
        throw createAbortError(fileAbortController.reason);
      }

      if (!uploadUrl || isPresignedUrlExpired(uploadUrl)) {
        uploadUrl = await getFreshPartUploadUrl(fileItem, part);
        part.uploadUrl = uploadUrl;
      }

      return await uploadPartToS3(
        part.blob,
        onProgress,
        uploadUrl,
        fileAbortController,
      );
    } catch (error) {
      if (retryCount >= MAX_RETRIES || error?.code === 'ABORTED') {
        throw error;
      }

      let nextUploadUrl = uploadUrl;

      if (shouldRefreshPresignedUrl(uploadUrl, error)) {
        nextUploadUrl = await getFreshPartUploadUrl(fileItem, part);
        part.uploadUrl = nextUploadUrl;
      }

      const retryDelay = getRetryDelay(retryCount);
      beeXLogger.warn(
        `UploadVideoCameraV2 - Retry part ${part?.partNumber} (${retryCount + 1}/${MAX_RETRIES}) in ${retryDelay}ms`,
        error,
      );

      await sleep(retryDelay);

      return handleUploadPartToS3WithRetry(
        fileItem,
        part,
        onProgress,
        fileAbortController,
        retryCount + 1,
        nextUploadUrl,
      );
    }
  };

  const runTasksWithConcurrency = async (
    items,
    worker,
    maxConcurrency = MAX_CONCURRENT_PARTS,
  ) => {
    const results = new Array(items.length);
    let nextIndex = 0;

    const runWorker = async () => {
      while (true) {
        const currentIndex = nextIndex;
        nextIndex += 1;

        if (currentIndex >= items.length) {
          return;
        }

        const currentItem = items[currentIndex];

        try {
          const value = await worker(currentItem, currentIndex);
          results[currentIndex] = {
            status: 'fulfilled',
            value,
            item: currentItem,
          };
        } catch (error) {
          results[currentIndex] = {
            status: 'rejected',
            reason: error,
            item: currentItem,
          };
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(maxConcurrency, items.length) }, () =>
        runWorker(),
      ),
    );

    return results;
  };

  const handleCompleteMultipartUpload = async (fileData, completedParts) => {
    try {
      const completeData = {
        namespace,
        objectName: fileData.objectName,
        uploadId: fileData.parts[0].uploadId,
        credentialId: fileData.credentialId,
        parts: completedParts
          .filter((part) => part.etag)
          .map((part) => ({
            partNumber: part.partNumber,
            etag: part.etag,
          })),
      };

      beeXLogger.debug(
        'UploadVideoCameraV2 - handleCompleteMultipartUpload - completeData: ',
        completeData,
      );

      const completeResponse = await completeUploadMultipartApi(
        cameraToken,
        {},
        completeData,
      );

      beeXLogger.debug(
        'UploadVideoCameraV2 - handleCompleteMultipartUpload - completeResponse: ',
        completeResponse,
      );

      return completeResponse;
    } catch (error) {
      beeXLogger.error(
        'UploadVideoCameraV2 - handleCompleteMultipartUpload - error: ',
        error,
      );
      throw error;
    }
  };

  const handleCheckCompleteUploadVideo = async (fileData) => {
    try {
      const checkData = {
        namespace,
        objectName: fileData.objectName,
        credentialId: fileData.credentialId,
      };

      beeXLogger.debug(
        'UploadVideoCameraV2 - handleCheckCompleteUploadVideo - checkData: ',
        checkData,
      );

      const checkResponse = await checkCompleteUploadVideoApi(
        cameraToken,
        {},
        checkData,
      );

      beeXLogger.debug(
        'UploadVideoCameraV2 - handleCheckCompleteUploadVideo - checkResponse: ',
        checkResponse,
      );

      return checkResponse;
    } catch (error) {
      beeXLogger.error(
        'UploadVideoCameraV2 - handleCheckCompleteUploadVideo - error: ',
        error,
      );
      throw error;
    }
  };

  const getListStorage = useCallback(
    (serial, dateRecord) => {
      let data = {
        serial,
        dateRecord: dateRecord.format('YYYY-MM-DD'),
        namespace,
      };
      let dataObj = {
        appServiceType: SERVICE.LIST_CAMERA_STORAGE_FILE,
        requestId: new Date().getTime(),
        body: JSON.stringify(data),
      };

      dispatch(socketEmitEvent(dataObj));
    },
    [dispatch, namespace],
  );

  const handleUpload = async (fileUploadData) => {
    try {
      beeXLogger.debug(
        'UploadVideoCameraV2 - handleUpload - Starting upload for files:',
        fileUploadData,
      );

      for (const fileItem of fileUploadData) {
        const fileId = fileItem?.fileId || fileItem?.id;
        const currentFileIndex = form
          .getFieldValue('listFileVideo')
          ?.findIndex((item) => item.id === fileId);

        const fileAbortController = handleFileAbortController(fileId);
        if (fileAbortController) {
          fileAbortController.isCancelled = false;
          fileAbortController.reason = null;
        }

        const fileProgressTracking = {};
        (fileItem.parts || []).forEach((part) => {
          fileProgressTracking[part.partNumber] = 0;
        });

        try {
          beeXLogger.debug(
            `UploadVideoCameraV2 - Starting upload for file: ${fileItem.fileId}`,
          );

          form.setFields([
            {
              name: ['listFileVideo', currentFileIndex, 'status'],
              value: {
                loadingPercent: 0,
                isUploadSuccess: null,
                error: null,
              },
            },
          ]);

          const partUploadResults = await runTasksWithConcurrency(
            fileItem.parts || [],
            async (part) => {
              try {
                const etag = await handleUploadPartToS3WithRetry(
                  fileItem,
                  part,
                  (percent) => {
                    if (fileAbortController?.isCancelled) {
                      return;
                    }

                    fileProgressTracking[part.partNumber] = percent;

                    let totalProgress = 0;
                    Object.keys(fileProgressTracking).forEach((partNum) => {
                      const partNumber = Number(partNum);
                      const partIndex = (fileItem.parts || []).findIndex(
                        (p) => p.partNumber === partNumber,
                      );
                      if (partIndex !== -1) {
                        const currentPart = fileItem.parts[partIndex];
                        const partWeight =
                          currentPart?.blob?.size / fileItem?.originFile?.size;
                        const partProgress = fileProgressTracking[partNum];
                        totalProgress += partWeight * partProgress;
                      }
                    });

                    const finalPercent = Math.min(
                      Math.round(totalProgress),
                      100,
                    );

                    form.setFields([
                      {
                        name: ['listFileVideo', currentFileIndex, 'status'],
                        value: {
                          loadingPercent: finalPercent,
                          isUploadSuccess: null,
                          error: null,
                        },
                      },
                    ]);
                  },
                  fileAbortController,
                );

                return {
                  partNumber: part.partNumber,
                  etag,
                };
              } catch (partError) {
                beeXLogger.error(
                  `UploadVideoCameraV2 - Part ${part.partNumber} failed for file ${fileItem.fileId}`,
                  partError,
                );
                throw partError;
              }
            },
            MAX_CONCURRENT_PARTS,
          );

          const failedPartResults = partUploadResults.filter(
            (result) => result.status === 'rejected',
          );

          if (fileAbortController?.isCancelled) {
            throw createAbortError(fileAbortController.reason);
          }

          if (failedPartResults.length > 0) {
            const failedParts = failedPartResults
              .map((result) => result?.item?.partNumber)
              .filter((partNumber) => partNumber !== undefined)
              .join(', ');

            throw new Error(
              failedParts
                ? `Failed to upload part(s): ${failedParts}`
                : 'Failed to upload one or more parts',
            );
          }

          const completedParts = partUploadResults
            .filter((result) => result.status === 'fulfilled')
            .map((result) => result.value);

          beeXLogger.debug(
            `UploadVideoCameraV2 - All parts uploaded for file ${fileItem.fileId}, calling complete API...`,
          );

          const completeResponse = await handleCompleteMultipartUpload(
            fileItem,
            completedParts,
          );

          if (completeResponse?.status === 200) {
            beeXLogger.debug(
              `UploadVideoCameraV2 - Complete multipart successful, checking upload status...`,
            );

            const checkResponse =
              await handleCheckCompleteUploadVideo(fileItem);

            if (checkResponse?.status === 200 && checkResponse?.data?.exists) {
              beeXLogger.debug(
                `UploadVideoCameraV2 - File ${fileItem.fileId} uploaded and verified successfully, uploading thumbnail...`,
              );

              const timestamp = getUploadTimestampFromFileTime(fileItem?.time);
              const thumbnailUrl = await handleGetThumbnailAndUpload(
                fileItem,
                timestamp,
              );
              await handleReportUpload(
                fileItem,
                thumbnailUrl,
                fileItem.videoDuration,
                moment(timestamp).add(7, 'hours').valueOf(),
              );

              form.setFields([
                {
                  name: ['listFileVideo', currentFileIndex, 'status'],
                  value: {
                    loadingPercent: 100,
                    isUploadSuccess: true,
                    error: null,
                  },
                },
              ]);

              beeXLogger.debug(
                `UploadVideoCameraV2 - File ${fileItem.fileId} completed with thumbnail and report`,
              );
            } else {
              throw new Error(
                'File verification failed - file does not exist on server',
              );
            }
          } else {
            throw new Error(
              completeResponse?.message || 'Complete multipart upload failed',
            );
          }
        } catch (err) {
          beeXLogger.error(
            `UploadVideoCameraV2 - Error uploading file ${fileItem.fileId}:`,
            err,
          );

          form.setFields([
            {
              name: ['listFileVideo', currentFileIndex, 'status'],
              value: {
                loadingPercent: null,
                isUploadSuccess: false,
                error:
                  (err?.code === 'ABORTED' &&
                    (err?.message || t('Upload cancelled by user'))) ||
                  err.message ||
                  t('There was an error while uploading the file'),
              },
            },
          ]);
        } finally {
          clearFileAbortController(fileId);
        }
      }

      const allFilesUploaded = form
        .getFieldValue('listFileVideo')
        ?.every((file) => file.status.isUploadSuccess);

      if (allFilesUploaded) {
        setIsUploadFilesSuccess(true);
        beeXLogger.debug(
          'UploadVideoCameraV2 - All files uploaded successfully!',
        );
        setTimeout(
          () => getListStorage(uploadFilesCamera?.serial, uploadFilesDate),
          500,
        );
      } else {
        const failedFiles = form
          .getFieldValue('listFileVideo')
          ?.filter((file) => file.status.error);
        beeXLogger.debug(
          'UploadVideoCameraV2 - Some files failed:',
          failedFiles,
        );
      }

      setIsLoad(false);
      uploadInProgressRef.current = false;
      setFilePreSignUrl([]);
    } catch (err) {
      beeXLogger.error(
        'UploadVideoCameraV2 - handleUpload - Critical error:',
        err,
      );
      setIsLoad(false);
      uploadInProgressRef.current = false;
      setFilePreSignUrl([]);
    }
  };

  const handleChangeFilesUpload = async (e) => {
    const listVideos = [...e.target.files];
    beeXLogger.debug(
      'UploadVideoCameraV2 - handleChangeFilesUpload - listVideos: ',
      listVideos,
    );

    if (listVideos.length > 1000) {
      openMessage(NOTIFIES.WARNING, t('Only allow uploading up to 1000 files'));
      return;
    }
    setIsLoadingReadFiles(true);

    try {
      const listStartTime = [];
      const listVideoDuration = await Promise.all(
        listVideos.map((videoFile) => getVideoDuration(videoFile)),
      );
      const videoParts = listVideos?.map((videoFile) => ({
        fileId:
          videoFile?.name + '_' + uploadFilesCamera?.serial + '_' + nanoid(),
        originFile: videoFile,
        parts: sliceFileToMultiParts(videoFile),
      }));
      beeXLogger.debug(
        'UploadVideoCameraV2 - handleChangeFilesUpload - videoParts: ',
        videoParts,
      );
      beeXLogger.debug(
        'UploadVideoCameraV2 - handleChangeFilesUpload - listVideoDuration: ',
        listVideoDuration,
      );

      for (let index = 0; index < listVideos.length; index++) {
        if (index === 0) {
          listStartTime.push(findLastStartTime());
        } else {
          listStartTime.push(
            listStartTime[index - 1]
              .clone()
              .add(
                Math.floor(listVideoDuration[index - 1] / 60) + 1,
                'minutes',
              ),
          );
        }
      }
      beeXLogger.debug(
        'UploadVideoCameraV2 - handleChangeFilesUpload - listStartTime: ',
        listStartTime,
      );

      const listSortedVideo = [];
      beeXLogger.debug(
        'UploadVideoCameraV2 - handleChangeFilesUpload - uploadFilesCamera: ',
        uploadFilesCamera,
      );

      const newVideoParts = await Promise.all(
        videoParts?.map(async (item) => {
          const parts = item?.parts;
          const objectName = `${moment(uploadFilesDate).format('YYYY/MM/DD')}/${uploadFilesCamera?.serial}/${
            uploadFilesCamera?.serial
          }_${moment(uploadFilesDate).valueOf()}_133_${moment(uploadFilesDate).format('YYYY-MM-DD-HH-mm')}.mp4`;
          const res =
            (await getUploadMultiIdApi(
              cameraToken,
              {},
              {
                contentType: 'video/mp4',
                namespace,
                objectName,
              },
            )) || {};

          return {
            ...item,
            objectName,
            credentialId: res?.data?.credentialId,
            bucket: res?.data?.bucket,
            parts: parts?.map((part) => ({
              ...part,
              uploadId: res?.data?.uploadId,
            })),
          };
        }) || [],
      );

      beeXLogger.debug(
        'UploadVideoCameraV2 - handleChangeFilesUpload - newVideoParts: ',
        newVideoParts,
      );

      newVideoParts
        ?.map((videoPartsItem, index) => {
          const file = videoPartsItem?.originFile;
          beeXLogger.debug(
            'UploadVideoCameraV2 - handleChangeFilesUpload - file: ',
            file,
          );
          return {
            ...videoPartsItem,
            id: videoPartsItem?.fileId || index,
            time: listStartTime[index],
            tags: file.name ? [file.name] : [],
            file,
            videoDuration: listVideoDuration[index],
            momentVideoDuration: listVideoDuration[index]
              ? moment.utc(listVideoDuration[index] * 1000)
              : null,
            status: {
              loadingPercent: null,
              isUploadSuccess: null,
              error: null,
            },
          };
        })
        .forEach((item) => {
          if (item.videoDuration === null) listSortedVideo.unshift(item);
          else listSortedVideo.push(item);
        });
      beeXLogger.debug(
        'UploadVideoCameraV2 - handleChangeFilesUpload - listSortedVideo: ',
        listSortedVideo,
      );

      form.setFieldsValue({
        listFileVideo: listSortedVideo,
      });

      setIsSubmittedForm(false);
    } catch (err) {
      beeXLogger.debug(
        'UploadVideoCameraV2 - handleChangeFilesUpload - err: ',
        err,
      );
      openMessage(NOTIFIES.WARNING, err.message);
    }

    setIsLoadingReadFiles(false);
  };

  useEffect(() => {
    if (filePreSignUrl?.length > 0 && uploadInProgressRef.current) {
      handleUpload(filePreSignUrl);
    }
  }, [filePreSignUrl]);

  const handleCancelUpload = () => {
    Object.keys(fileAbortControllersRef.current).forEach((fileId) => {
      abortFileUploadById(fileId);
    });

    dispatch(getResetUploadFileReturn());
    setIsCollapse(false);
    dispatch(setOpenUploadFilesModal(false));
    dispatch(setUploadFilesCamera(null));
    dispatch(setUploadFilesDate(null));
    dispatch(setUploadFilesOldVideos(null));
    setFilePreSignUrl([]);
    fileAbortControllersRef.current = {};
    uploadInProgressRef.current = false;
  };

  const handleCollapseUpload = () => {
    setIsCollapse(true);
  };

  const progressUploadTitle = `${
    (Array.isArray(form.getFieldValue('listFileVideo'))
      ? form.getFieldValue('listFileVideo')
      : []
    )?.filter((item) => item.status.isUploadSuccess)?.length || 0
  }/${form.getFieldValue('listFileVideo')?.length || 0} files`;

  return (
    <>
      <Modal
        title={t('Upload video') + ` (${uploadFilesCamera?.name})` ?? ''}
        className="modal-upload-video"
        style={{ top: 64 }}
        width="80vw"
        bodyStyle={{ minHeight: '35vh' }}
        visible={!isCollapse}
        onCancel={
          isUploadFilesSuccess || !form.getFieldValue('listFileVideo')?.length
            ? handleCancelUpload
            : handleCollapseUpload
        }
        footer={null}
        maskClosable={false}
      >
        <div className="upload-video-camera-container">
          <Form form={form} {...layout} onFinish={handleSubmitUploadVideos}>
            <p>
              <span style={{ fontSize: 15 }}>Date:</span>{' '}
              {moment(uploadFilesDate).format('DD-MM-YYYY')}
            </p>

            <Button
              className="button-upload"
              onClick={() => inputUploadFileRef.current.click()}
              icon={<UploadOutlined />}
              disabled={isLoad}
            >
              {t('Upload Files')}
            </Button>
            <Button
              className="button-upload"
              onClick={() => inputUploadFolderRef.current.click()}
              icon={<FolderOutlined />}
              disabled={isLoad}
            >
              {t('Upload Folder')}
            </Button>
            <input
              type="file"
              multiple
              onChange={(e) => {
                handleChangeFilesUpload(e);
                inputUploadFileRef.current.value = '';
              }}
              className="input-upload"
              id="input-upload-file"
              ref={inputUploadFileRef}
              accept="video/mp4,video/x-m4v,video/*"
            />
            <input
              type="file"
              webkitdirectory="true"
              onChange={(e) => {
                handleChangeFilesUpload(e);
                inputUploadFolderRef.current.value = '';
              }}
              mozdirectory="true"
              multiple
              className="input-upload"
              ref={inputUploadFolderRef}
              accept="video/mp4,video/x-m4v,video/*"
            />
            {isLoadingReadFiles && (
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            )}

            <Form.List name="listFileVideo">
              {(fields, { remove }) => (
                <>
                  {form.getFieldValue('listFileVideo')?.length > 0 && (
                    <Text>Uploaded: {progressUploadTitle}</Text>
                  )}
                  {fields.length > 0 && (
                    <div className="table-upload-container">
                      <table>
                        <thead>
                          <tr>
                            <td>{t('Start time')}</td>
                            <td>{t('Tags')}</td>
                            <td>{t('Info')}</td>
                            <td>{t('Video duration')}</td>
                            <td>{t('Upload status')}</td>
                            <td>{t('Actions')}</td>
                          </tr>
                        </thead>
                        <tbody>
                          {fields.map((field) => {
                            const currentFileData =
                              form.getFieldValue('listFileVideo')?.[field.name];
                            const currentFileId =
                              currentFileData?.fileId || currentFileData?.id;

                            return (
                              <tr key={field.key}>
                                <td>
                                  <div className="align-center">
                                    <Form.Item
                                      {...field}
                                      name={[field.name, 'time']}
                                      rules={[
                                        {
                                          required: true,
                                          message: t('Required'),
                                        },
                                      ]}
                                    >
                                      <TimePicker
                                        style={{ width: '100%' }}
                                        format="HH:mm"
                                        placeholder={t('Select time')}
                                        disabled={isSubmittedForm}
                                      />
                                    </Form.Item>
                                  </div>
                                </td>
                                <td>
                                  <Form.Item
                                    {...field}
                                    name={[field.name, 'tags']}
                                  >
                                    <InputTag
                                      numberTag={10}
                                      nameAdd="Add tag"
                                      containerStyle={{ marginLeft: 0 }}
                                      disabled={isSubmittedForm}
                                    />
                                  </Form.Item>
                                </td>

                                <td>
                                  <Form.Item
                                    {...field}
                                    name={[field.name, 'file']}
                                  >
                                    <FileInfo />
                                  </Form.Item>
                                </td>
                                <td>
                                  <Form.Item
                                    {...field}
                                    name={[field.name, 'momentVideoDuration']}
                                    rules={[
                                      {
                                        validator: (_, value) => {
                                          if (value) {
                                            return value.hour() * 60 * 60 +
                                              value.minute() * 60 +
                                              value.second() >=
                                              1
                                              ? Promise.resolve()
                                              : Promise.reject(
                                                  new Error(
                                                    t('At least 1 second'),
                                                  ),
                                                );
                                          }
                                          return Promise.reject(
                                            new Error(t('Required')),
                                          );
                                        },
                                      },
                                    ]}
                                  >
                                    <TimePicker
                                      style={{ width: '100%' }}
                                      format="HH:mm:ss"
                                      placeholder={t('Duration')}
                                      disabled={isSubmittedForm}
                                    />
                                  </Form.Item>
                                </td>

                                <td>
                                  {
                                    <Form.Item
                                      {...field}
                                      name={[field.name, 'status']}
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <UploadStatus />
                                    </Form.Item>
                                  }
                                </td>
                                <td className="table-actions">
                                  {
                                    <Form.Item
                                      {...field}
                                      name={[field.name, 'status']}
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <RemoveVideoFileField
                                        onClick={() => remove(field.name)}
                                        onCancelUpload={() =>
                                          handleCancelSingleFileUpload(
                                            currentFileId,
                                          )
                                        }
                                        isLoad={isLoad}
                                      />
                                    </Form.Item>
                                  }
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="update-camera-footer">
                    {form
                      .getFieldValue('listFileVideo')
                      ?.some((item) => !item.status.isUploadSuccess) && (
                      <>
                        <Button onClick={handleCancelUpload}>
                          {t('Cancel')}
                        </Button>
                        <Button
                          onClick={handleCollapseUpload}
                          icon={<FullscreenExitOutlined />}
                          type="primary"
                        >
                          {t('Collapse')}
                        </Button>
                        <Form.Item shouldUpdate={true}>
                          {() => (
                            <Button
                              icon={<SaveOutlined />}
                              htmlType="submit"
                              type="primary"
                              loading={isLoad}
                              disabled={
                                isLoad ||
                                form
                                  .getFieldsError()
                                  .some((item) => item.errors.length > 0)
                              }
                            >
                              {t('Submit')}
                            </Button>
                          )}
                        </Form.Item>
                      </>
                    )}
                  </div>
                </>
              )}
            </Form.List>
          </Form>
        </div>
      </Modal>

      <div
        className="upload-file-collapse-container"
        style={{ display: isCollapse ? 'inline-flex' : 'none' }}
        onClick={() => {
          setIsCollapse(false);
          dispatch(setOpenUploadFilesModal(true));
        }}
      >
        {isLoad && <UploadFileSpinner />}
        <div
          className="upload-file-collapse-title"
          style={{
            backgroundColor: isUploadFilesSuccess
              ? 'green'
              : error
                ? '#ff4d4d'
                : 'rgba(240, 156, 60, 0.9)',
          }}
        >
          <span>
            {isUploadFilesSuccess
              ? 'Upload done!'
              : error
                ? 'Upload fail!'
                : progressUploadTitle}
          </span>
        </div>
      </div>
    </>
  );
};

const FileInfo = ({ value }) => (
  <>
    <div>Name: {value.name}</div>
    <div>Size: {(value.size / (1024 * 1024)).toFixed(2)} MB</div>
  </>
);

const UploadStatus = ({ value }) => {
  return (
    <>
      {value.isUploadSuccess && (
        <>
          Success <CheckCircleFilled className="icon-done" />
        </>
      )}

      {value.loadingPercent !== null &&
        value.loadingPercent !== 100 &&
        !value.isUploadSuccess &&
        !value.error && (
          <>
            <Progress percent={value.loadingPercent} showInfo={false} />
            {value.loadingPercent}%
          </>
        )}

      {value.loadingPercent === 100 &&
        value.isUploadSuccess === null &&
        !value.error && (
          <>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />}
            />{' '}
            <span>Saving...</span>
          </>
        )}

      {value.error && !value.isUploadSuccess && (
        <div>
          Fail
          <WarningFilled className="icon-error" />
          <div>{value.error}</div>
        </div>
      )}
    </>
  );
};

const RemoveVideoFileField = ({ value, onClick, onCancelUpload, isLoad }) => {
  const isUploadingPart =
    value?.loadingPercent !== null &&
    value?.loadingPercent < 100 &&
    !value?.isUploadSuccess &&
    !value?.error;

  return (
    <>
      {isUploadingPart && (
        <Button type="link" danger onClick={onCancelUpload}>
          Cancel
        </Button>
      )}

      {!value?.isUploadSuccess && !isLoad && (
        <DeleteOutlined onClick={onClick} />
      )}
    </>
  );
};

const UploadFileSpinner = () => {
  return (
    <div class="upload-file-spinner">
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </div>
  );
};

export default memo(UploadVideoCameraV2);
